import { useTranslation } from 'react-i18next';

import { mapIntegrationProviderToDTO } from '@/modules/common/mappers/integration.mapper';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { MissingIntegrationNotice } from '@/modules/onboarding/common/components/resolve-duplicates-modal/missing-integration-notice';
import { ResolveOnboardingConflictsModal } from '@/modules/onboarding/common/components/resolve-duplicates-modal/resolve-onboarding-conflicts-modal';
import {
  MergeRequestBuilder,
  OnboardingResolveConflictAttributeRow,
} from '@/modules/onboarding/common/components/resolve-duplicates-modal/types';
import { invalidateOnboardingClientRelatedQueries } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';
import { removeDuplicatesByKey } from '@/modules/onboarding/common/utils/reconciliation-helpers';

import { mergeClients } from '../../api/clients';
import { Client } from '../../types/clients.types';
import { MergeClientsRequest } from '../../types/request/merge-clients.request';

const CLIENTS_I18N_NAMESPACE = 'authenticated.onboarding.clients';

interface IProps {
  selectedItems: Client[];
  onSuccess?: () => void;
  connectedIntegrations: IntegrationServices[];
  disabled?: boolean;
  disabledTooltip?: string;
}

function getAttributeRows(
  t: (key: string) => string
): OnboardingResolveConflictAttributeRow<Client>[] {
  return [
    {
      key: 'name',
      label: t(`${CLIENTS_I18N_NAMESPACE}:duplicates.name`),
      placeholder: t(`${CLIENTS_I18N_NAMESPACE}:duplicates.selectName`),
      getSelectOptions: items =>
        removeDuplicatesByKey(items, 'name').map(item => ({
          value: item.name,
          label: item.name,
        })),
      getDisplayValue: item => item.name,
      isEmpty: item => !item.name.trim(),
    },
  ];
}

const mergeRequestBuilder: MergeRequestBuilder<Client> = {
  build: (formData, selectedItems, integrationsToUse) => {
    const selectedName = formData.name;
    const selectedClient = selectedItems.find(client => client.name === selectedName);

    const request: MergeClientsRequest = {
      idsToMerge: selectedItems.map(item => item.id),
      resultingClient: {
        name: selectedName,
        importedFromFilename: selectedClient?.importedFromFilename,
        integrationsToUse: integrationsToUse.map(i => ({
          integration: mapIntegrationProviderToDTO(i.integration as IntegrationServices),
          sourceClientId: i.sourceId,
        })),
      },
    };

    return request;
  },
};

export function ResolveOnboardingClientsConflictsModal({
  selectedItems,
  onSuccess,
  connectedIntegrations,
  disabled,
  disabledTooltip,
}: IProps) {
  const { t } = useTranslation();

  const attributeRows = getAttributeRows(t);

  return (
    <ResolveOnboardingConflictsModal
      selectedItems={selectedItems}
      onSuccess={onSuccess ?? (() => {})}
      attributeRows={attributeRows}
      integrationsList={connectedIntegrations}
      mergeMutationFn={mergeClients}
      invalidateQueries={invalidateOnboardingClientRelatedQueries}
      mergeRequestBuilder={mergeRequestBuilder}
      i18nNamespace={CLIENTS_I18N_NAMESPACE}
      personLabelI18nKey={`${CLIENTS_I18N_NAMESPACE}:duplicates.person`}
      resultingEntityI18nKey={`${CLIENTS_I18N_NAMESPACE}:duplicates.resultingEntity`}
      renderFooterNotice={() => <MissingIntegrationNotice i18nNamespace={CLIENTS_I18N_NAMESPACE} />}
      triggerDisabled={disabled}
      triggerDisabledTooltip={disabledTooltip}
    />
  );
}
